import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { ConfirmationProvider } from './contexts/ConfirmationContext';
import Header from './components/Header';
import Footer from './components/Footer';
import TrailingSlashRedirect from './components/TrailingSlashRedirect';
import HomePage from './components/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import GoogleLoginCallbackPage from './pages/GoogleLoginCallbackPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import SearchPage from './pages/SearchPage';
import SeatedRegistrationPage from './pages/SeatedRegistrationPage';
import ChatPage from './pages/ChatPage';
import AddonDetailPage from './pages/AddonDetailPage';
import CartPage from './pages/CartPage';
import ProfilePage from './pages/ProfilePage';
import EditProfilePage from './pages/EditProfilePage';
import PaymentStatusPage from './pages/PaymentStatusPage';
import PaymentConfirmationPage from './pages/PaymentConfirmationPage';
import PaymentCallbackPage from './pages/PaymentCallbackPage';
import TransferTicketsPage from './pages/TransferTicketsPage';
import VerifyPassPage from './pages/VerifyPassPage';
import SecurePassAccessPage from './pages/SecurePassAccessPage';
import ScannerDashboardPage from './pages/scanner/ScannerDashboardPage';
import QRCodeScannerPage from './pages/scanner/QRCodeScannerPage';
import EventStatsPage from './pages/scanner/EventStatsPage';
import EventAttendeesPage from './pages/scanner/EventAttendeesPage';
import AddonScannerPage from './pages/scanner/AddonScannerPage';
import AddonStatsPage from './pages/scanner/AddonStatsPage';
import AddonOrdersPage from './pages/scanner/AddonOrdersPage';
import ActivityScannerPage from './pages/scanner/ActivityScannerPage';
import OrganizationMicrositePage from './pages/OrganizationMicrositePage';
import { ErrorBoundary, NotFoundPage, ServerErrorPage, UnauthorizedPage, DjangoFallback404 } from './components/ErrorPages';
import GlobalChatWidget from './components/chat/GlobalChatWidget';
import './App.scss';

const App: React.FC = () => {
  useEffect(() => {
    // App initialization
    // Any app-wide initialization can go here
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <ConfirmationProvider>
          <Router>
        <TrailingSlashRedirect>
        <div className="App d-flex flex-column min-vh-100">
          <Header />

          <main className="flex-grow-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              
              {/* Authentication routes */}
              <Route path="/login/" element={<LoginPage />} />
              <Route path="/signup/" element={<SignupPage />} />
              <Route path="/accounts/login/" element={<LoginPage />} />
              <Route path="/accounts/google/login/callback/" element={<GoogleLoginCallbackPage />} />
              <Route path="/forgot-password/" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:uid/:token/" element={<ResetPasswordPage />} />
              
              {/* Search route */}
              <Route path="/search/" element={<SearchPage />} />
              
              {/* Event routes */}
              <Route path="/events/" element={<EventsPage />} />
              <Route path="/events/:slug/" element={<EventDetailPage />} />
              <Route path="/events/:slug/seated/" element={<SeatedRegistrationPage />} />
              <Route path="/events/:slug/seated/:categoryId/" element={<SeatedRegistrationPage />} />
              
              {/* Addon routes */}
              <Route path="/addons/:addonId/" element={<AddonDetailPage />} />

              {/* Cart route */}
              <Route path="/cart/" element={<CartPage />} />
              
              {/* Profile routes */}
              <Route path="/profile/:username/" element={<ProfilePage />} />
              <Route path="/profile/:username/edit/" element={<EditProfilePage />} />
              
              {/* Chat routes */}
              <Route path="/chat/" element={<ChatPage />} />
              <Route path="/chat/:roomId/" element={<ChatPage />} />
              
              {/* Payment routes */}
              <Route path="/payment/:cartId/status/" element={<PaymentStatusPage />} />
              <Route path="/checkout/:cartId/confirmation/" element={<PaymentConfirmationPage />} />
              <Route path="/checkout/:cartId/:gateway/callback/" element={<PaymentCallbackPage />} />
              <Route path="/orders/checkout/:cartId/confirmation/" element={<PaymentConfirmationPage />} />
              
              {/* Pass management routes */}
              <Route path="/transfer/" element={<TransferTicketsPage />} />
              <Route path="/verify/" element={<VerifyPassPage />} />
              <Route path="/secure-pass/" element={<SecurePassAccessPage />} />
              
              {/* Scanner routes */}
              <Route path="/scanner/" element={<ScannerDashboardPage />} />
              <Route path="/scanner/event/:eventSlug/qr/" element={<QRCodeScannerPage />} />
              <Route path="/scanner/event/:eventSlug/stats/" element={<EventStatsPage />} />
              <Route path="/scanner/event/:eventSlug/attendees/" element={<EventAttendeesPage />} />
              <Route path="/scanner/event/:eventSlug/addon/" element={<AddonScannerPage />} />
              <Route path="/scanner/event/:eventSlug/addon/stats/" element={<AddonStatsPage />} />
              <Route path="/scanner/event/:eventSlug/addon/orders/" element={<AddonOrdersPage />} />
              <Route path="/scanner/activity/:activityPlaceSlug/qr/" element={<ActivityScannerPage />} />
              
              {/* Django-managed orgs dashboard */}
              <Route path="/orgs/" element={<DjangoFallback404 />} />

              {/* Organization microsite - single segment slug (must be after all specific routes) */}
              <Route path="/:orgSlug/" element={<OrganizationMicrositePage />} />

              {/* Error pages */}
              <Route path="/error/403/" element={<UnauthorizedPage />} />
              <Route path="/error/500/" element={<ServerErrorPage />} />
              
              {/* 404 - Catch all route with Django fallback */}
              <Route path="*" element={<DjangoFallback404 />} />
            </Routes>
          </main>

          <Footer />
          
          {/* Global Chat Widget - Available on all pages */}
          <GlobalChatWidget />
        </div>
        </TrailingSlashRedirect>
        </Router>
        </ConfirmationProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
